import { DEFAULT_LIMITS, type Priority, type TicketDraft, type TicketType } from '@lixionary-jira/domain';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useAssistantStatus } from '../../api/assistant';
import { ApiError, api } from '../../api/client';
import { useAction, useProject } from '../../api/hooks';
import { useIsAdmin } from '../../api/session';
import { useCrumbs } from '../../layout/crumbs';
import { Button, LinkButton } from '../../ui/Button';
import { Alert, ErrorAlert, PageUnavailable } from '../../ui/feedback';
import { Field, FormSection, Input, ListEditor, Segmented, Select, Textarea } from '../../ui/forms';
import { Markdown } from '../../ui/Markdown';
import { PageHeader, usePageTitle } from '../../ui/misc';
import { AssistantDraft } from './AssistantDraft';
import { PRIORITY_OPTIONS } from './bits';

type Mode = 'form' | 'assistant';

/**
 * TKT-01: issue or feature request; enters Pending with server-default priority. Requesters either fill in
 * the form (reviewed by the assistant in the background) or draft the ticket in a conversation with it.
 */
export function NewTicketPage() {
  const { key } = useParams();
  const project = useProject(key);
  const admin = useIsAdmin();
  const navigate = useNavigate();
  const assistant = useAssistantStatus();
  const [params, setParams] = useSearchParams();
  const assistantOn = !!assistant.data?.available;
  const mode: Mode = assistantOn && params.get('mode') === 'assistant' ? 'assistant' : 'form';
  const sessionId = params.get('conversation');
  const setMode = (m: Mode, conversation = sessionId) =>
    setParams(
      {
        ...(m === 'assistant' ? { mode: m } : {}),
        ...(conversation ? { conversation } : {}),
      },
      { replace: true },
    );
  /** Set when the form was prefilled from an assistant draft: submitting closes that conversation. */
  const [fromDraft, setFromDraft] = useState<string | null>(null);
  usePageTitle(project.data ? `${project.data.name} · Create ticket` : null);
  useCrumbs([
    { label: project.data?.name ?? key ?? '', to: `/projects/${key}` },
    { label: 'Board', to: `/projects/${key}/board` },
    { label: 'New ticket' },
  ]);
  const [type, setType] = useState<TicketType>('issue');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [preview, setPreview] = useState(false);
  const [steps, setSteps] = useState('');
  const [criteria, setCriteria] = useState<string[]>(['']);
  const [labels, setLabels] = useState('');
  const [priority, setPriority] = useState<Priority>('normal');
  const [files, setFiles] = useState<File[]>([]);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const applyDraft = (d: TicketDraft, conversation: string) => {
    setType(d.type);
    setTitle(d.title);
    setDescription(d.description);
    setSteps(d.stepsOrOutcome);
    setCriteria(d.acceptanceCriteria.length ? d.acceptanceCriteria : ['']);
    setLabels(d.labels.join(', '));
    setFromDraft(conversation);
    setMode('form', conversation);
  };
  const create = useAction(
    async () => {
      const body = {
        type,
        title,
        description,
        stepsOrOutcome: steps,
        acceptanceCriteria: criteria.filter((c) => c.trim()),
        labels: labels
          .split(',')
          .map((l) => l.trim())
          .filter(Boolean),
      };
      const r = fromDraft
        ? await api.post<{ number: number; key: string }>(
            `/projects/${key}/assistant/sessions/${fromDraft}/submit`,
            body,
          )
        : await api.post<{ number: number; key: string }>(`/projects/${key}/tickets`, {
            ...body,
            ...(admin ? { priority } : {}),
          });
      const errors: string[] = [];
      for (const f of files) {
        const form = new FormData();
        form.append('file', f);
        await api
          .upload(`/projects/${key}/tickets/${r.number}/attachments`, form)
          .catch((e: unknown) =>
            errors.push(`${f.name}: ${e instanceof Error ? e.message : 'upload failed'}`),
          );
      }
      setUploadErrors(errors);
      return { ...r, uploadErrors: errors };
    },
    {
      success: (r) => `Submitted ${r.key}`,
      invalidate: [
        ['board', key],
        ['dashboard', key],
      ],
      onSuccess: (r) => r.uploadErrors.length === 0 && navigate(`/projects/${key}/tickets/${r.number}`),
    },
  );
  if (project.data?.archived) return <PageUnavailable />;
  const err = create.error instanceof ApiError ? create.error : null;
  return (
    <div className="page narrow">
      <PageHeader
        title="Create ticket"
        subtitle="Requests start in Pending. An admin reviews, clarifies and accepts them before any implementation."
      />
      {assistantOn && (
        <Segmented<Mode>
          label="How to create the ticket"
          value={mode}
          onChange={(m) => setMode(m)}
          options={[
            { value: 'form', label: 'Fill in the form' },
            { value: 'assistant', label: 'Draft with the assistant' },
          ]}
        />
      )}
      {mode === 'assistant' ? (
        <AssistantDraft
          projectKey={key!}
          sessionId={sessionId}
          model={assistant.data?.model ?? ''}
          onSession={(id) => setMode('assistant', id)}
          onEdit={applyDraft}
          onSubmitted={(t) => navigate(`/projects/${key}/tickets/${t.number}`)}
        />
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate(undefined);
          }}
        >
          {fromDraft ? (
            <Alert tone="info" title="Prefilled from your assistant conversation">
              Review and adjust anything before submitting. The conversation and its code analysis stay linked
              to the ticket.
            </Alert>
          ) : (
            assistant.data?.reviewOnCreate && (
              <p className="small muted">
                After you submit, the assistant checks the request against the project&apos;s code and
                suggests clarifications on the ticket. Your text is not changed unless you apply a suggestion.
              </p>
            )
          )}
          <FormSection title="Request">
            <Segmented
              label="Ticket type"
              value={type}
              onChange={setType}
              options={[
                { value: 'issue', label: 'Issue' },
                { value: 'feature_request', label: 'Feature request' },
              ]}
            />
            <Field label="Title" error={err?.fieldError('title')}>
              {(p) => (
                <Input
                  {...p}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={
                    type === 'issue'
                      ? 'e.g. Offline queue drops edits after token refresh'
                      : 'e.g. Export visit notes as PDF'
                  }
                  maxLength={200}
                  required
                />
              )}
            </Field>
            <Field
              label="Description"
              hint="Markdown. HTML and remote images are not rendered."
              error={err?.fieldError('description')}
            >
              {(p) => (
                <div className="stack sm">
                  <Segmented
                    label="Description mode"
                    value={preview ? 'preview' : 'write'}
                    onChange={(v) => setPreview(v === 'preview')}
                    options={[
                      { value: 'write', label: 'Write' },
                      { value: 'preview', label: 'Preview' },
                    ]}
                  />
                  {preview ? (
                    <div className="well">
                      <Markdown>{description || '_Nothing to preview_'}</Markdown>
                    </div>
                  ) : (
                    <Textarea
                      {...p}
                      rows={8}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      required
                    />
                  )}
                </div>
              )}
            </Field>
            <Field label={type === 'issue' ? 'Reproduction steps' : 'Intended outcome'} optional>
              {(p) => (
                <Textarea
                  {...p}
                  rows={4}
                  value={steps}
                  onChange={(e) => setSteps(e.target.value)}
                  placeholder={
                    type === 'issue'
                      ? '1. …  2. …  3. Expected vs actual'
                      : 'What a user can do once this ships'
                  }
                />
              )}
            </Field>
          </FormSection>
          <FormSection
            title="Acceptance criteria"
            description="Proposed criteria. An admin finalizes them; they are required at acceptance."
          >
            <ListEditor
              label="Criterion"
              items={criteria}
              onChange={setCriteria}
              addLabel="Add criterion"
              placeholder="Observable outcome"
            />
          </FormSection>
          <FormSection title="Details">
            <Field label="Labels" hint="Comma separated." optional>
              {(p) => (
                <Input
                  {...p}
                  value={labels}
                  onChange={(e) => setLabels(e.target.value)}
                  placeholder="sync, android"
                />
              )}
            </Field>
            {admin && !fromDraft ? (
              <Field label="Priority">
                {(p) => (
                  <Select {...p} value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
                    {PRIORITY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            ) : (
              <p className="small muted">New requests start at normal priority; admins prioritize.</p>
            )}
            <Field
              label="Attachments"
              hint={`Images, text, logs, CSV or PDF · up to ${Math.round(DEFAULT_LIMITS.uploadMaxBytes / 1048576)} MiB each. Files are scanned before anyone can download them.`}
              optional
            >
              {(p) => (
                <input
                  {...p}
                  type="file"
                  multiple
                  accept="image/*,.txt,.log,.csv,.md,.pdf"
                  onChange={(e) => setFiles([...(e.target.files ?? [])])}
                />
              )}
            </Field>
          </FormSection>
          {uploadErrors.length > 0 && create.data && (
            <Alert tone="warning" title={`${create.data.key} was created, but some files were refused`}>
              <ul>
                {uploadErrors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
              <a href={`/projects/${key}/tickets/${create.data.number}`}>Open {create.data.key}</a>
            </Alert>
          )}
          <ErrorAlert error={create.error && !err?.details.some((d) => d.field) ? create.error : null} />
          <div className="form-actions">
            <LinkButton to={`/projects/${key}/board`}>Cancel</LinkButton>
            <Button type="submit" loading={create.isPending}>
              Submit request
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
